import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(amount)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

function isLocalAppHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".local")
  );
}

/** Ensure env base URLs always include a scheme (Vercel env typos like `foo.vercel.app/` break `new URL`). */
export function normalizePublicAppBaseUrl(raw: string): string {
  const trimmed = raw.trim().replace(/\/$/, "");
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function readPublicAppUrlFromEnv(): string {
  const raw = process.env.NEXT_PUBLIC_APP_URL?.trim() ?? "";
  if (!raw) return "";
  return normalizePublicAppBaseUrl(raw);
}

export function isLocalAppUrl(url: string): boolean {
  try {
    return isLocalAppHostname(new URL(url).hostname);
  } catch {
    return true;
  }
}

/**
 * Base URL for auth redirects (magic link, OAuth).
 * - On **localhost**, always use the browser origin so dev matches the port you are running.
 * - On a **real deployed host**, never follow NEXT_PUBLIC_APP_URL when it still points at localhost
 *   (common misconfiguration) — use the page origin so Google OAuth and magic links return to production.
 * - Server-side / edge: prefer a non-local NEXT_PUBLIC_APP_URL when set.
 */
export function getAuthRedirectOrigin(): string {
  const fromEnv = readPublicAppUrlFromEnv();

  if (typeof window !== "undefined") {
    const origin = window.location.origin.replace(/\/$/, "");
    const host = window.location.hostname;
    if (isLocalAppHostname(host)) {
      return origin;
    }
    if (fromEnv && !isLocalAppUrl(fromEnv)) {
      return fromEnv;
    }
    return origin;
  }

  if (fromEnv && !isLocalAppUrl(fromEnv)) return fromEnv;
  return fromEnv;
}

/**
 * Customer-facing links (quote URL in emails, Stripe return URLs) for a Route Handler request.
 * If `NEXT_PUBLIC_APP_URL` is missing or left as localhost on a deployed host, old behavior
 * produced broken links — we prefer the real request origin for non-local hosts.
 *
 * On Vercel / reverse proxies, `x-forwarded-host` + `x-forwarded-proto` reflect the browser URL
 * even when `req.url` is an internal origin — important for OAuth redirect_uri.
 */
export function getPublicAppUrlFromRequest(req: Request): string {
  const envBase = readPublicAppUrlFromEnv();
  /**
   * Allowlist of hostnames we'll trust from inbound headers. Without
   * this, a request with `Host: evil.com` (or `X-Forwarded-Host:
   * evil.com`) flowing through any non-Vercel proxy could poison the
   * URL we put into outbound emails and Stripe return URLs. Vercel
   * already normalises these on its edge, but we cheap-add a
   * deny-by-default for any self-hosted reverse-proxy deployment.
   */
  const allowedHosts = new Set<string>(["jorvioquotes.com", "www.jorvioquotes.com"]);
  if (envBase) {
    try {
      allowedHosts.add(new URL(envBase).hostname);
    } catch {
      /* ignore */
    }
  }
  const isAllowedDeployedHost = (host: string): boolean => {
    const h = host.split(":")[0]?.toLowerCase() ?? "";
    if (allowedHosts.has(h)) return true;
    /** Preview deploys + branch URLs on Vercel. */
    if (h.endsWith(".vercel.app")) return true;
    return false;
  };

  const fwdHostRaw = req.headers.get("x-forwarded-host")?.split(",")[0]?.trim();
  const fwdProtoRaw = req.headers.get("x-forwarded-proto")?.split(",")[0]?.trim().toLowerCase();
  if (fwdHostRaw) {
    const fwdHostOnly = fwdHostRaw.split(":")[0] ?? fwdHostRaw;
    if (!isLocalAppHostname(fwdHostOnly) && isAllowedDeployedHost(fwdHostRaw)) {
      const scheme = fwdProtoRaw === "http" ? "http" : "https";
      return `${scheme}://${fwdHostRaw}`.replace(/\/$/, "");
    }
  }

  const hostHdr = req.headers.get("host")?.split(",")[0]?.trim();
  if (hostHdr) {
    const hostOnly = hostHdr.split(":")[0] ?? hostHdr;
    if (!isLocalAppHostname(hostOnly) && isAllowedDeployedHost(hostHdr)) {
      try {
        const ru = new URL(req.url);
        if (isLocalAppHostname(ru.hostname)) {
          return `https://${hostHdr}`.replace(/\/$/, "");
        }
      } catch {
        /* ignore */
      }
    }
  }

  let origin = "";
  try {
    origin = new URL(req.url).origin;
  } catch {
    return envBase || "http://localhost:3000";
  }
  try {
    const host = new URL(origin).hostname;
    const isLocal =
      host === "localhost" || host === "127.0.0.1" || host === "[::1]" || host.endsWith(".local");
    // On localhost, always prefer the request origin (scheme + port) so Supabase emailRedirectTo
    // matches the browser. NEXT_PUBLIC_APP_URL is often https://localhost while dev runs on http.
    if (isLocal) {
      return origin || envBase || "http://localhost:3000";
    }
    if (origin) return origin;
  } catch {
    return envBase || origin || "http://localhost:3000";
  }
  return envBase || origin || "http://localhost:3000";
}

/**
 * Origin for Supabase `emailRedirectTo` from a Route Handler. Prefer the browser `Origin` (or
 * `Referer`) so the value matches the tab the user signed up from; `req.url` alone can differ
 * (e.g. host vs 127.0.0.1, reverse proxy) and Supabase rejects redirects not on the allow list.
 */
export function getAuthEmailRedirectOrigin(req: Request): string {
  const tryParse = (raw: string | null): string | null => {
    if (!raw?.trim()) return null;
    try {
      return new URL(raw.trim()).origin.replace(/\/$/, "");
    } catch {
      return null;
    }
  };
  const fromOrigin = tryParse(req.headers.get("origin"));
  if (fromOrigin) return fromOrigin;
  const fromReferer = tryParse(req.headers.get("referer"));
  if (fromReferer) return fromReferer;
  return getPublicAppUrlFromRequest(req);
}

/**
 * Origin that email clients can load (not localhost). Prefer `NEXT_PUBLIC_APP_URL` when it
 * points at a deployed host; else the request origin if the handler is hit on a public URL.
 */
export function getNonLocalPublicBaseUrlFromRequest(req: Request): string | null {
  const envBase = readPublicAppUrlFromEnv();
  if (envBase && !isLocalAppUrl(envBase)) return envBase;
  try {
    const origin = new URL(req.url).origin;
    if (!isLocalAppUrl(origin)) return origin;
  } catch {
    /* ignore */
  }
  return null;
}

/** Cron / background jobs — no Request; set `NEXT_PUBLIC_APP_URL` in production. */
export function getPublicAppUrlFromEnv(): string {
  return readPublicAppUrlFromEnv() || "http://localhost:3000";
}

export function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Shorter one-line stamp for dense tables (e.g. Settings team list). */
export function formatDateTimeCompact(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function toDateTimeLocalInput(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function fromDateTimeLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function splitIsoToDateAndTime(
  value: string | null | undefined
): { date: string; time: string } {
  if (!value) return { date: "", time: "" };
  const localValue = toDateTimeLocalInput(value);
  if (!localValue) return { date: "", time: "" };
  const [date = "", time = ""] = localValue.split("T");
  return { date, time };
}

export function combineDateAndTimeToIso(
  date: string | null | undefined,
  time: string | null | undefined,
  fallbackTime = "09:00"
): string | null {
  const safeDate = date?.trim() ?? "";
  if (!safeDate) return null;
  const safeTime = time?.trim() || fallbackTime;
  return fromDateTimeLocalInput(`${safeDate}T${safeTime}`);
}
